import * as THREE from "three";
import { OBJLoader, STLLoader } from "three-stdlib";
import { loadMesh } from "../../../core/meshLoader";
import { acquireMesh as acquireMeshFromCache } from "../../../runtime/assetCache";
import { getSceneState } from "../../../stores/sceneStore";
import { buildMaterial, createCollisionMaterial } from "../../materialFactory";
import { releaseAndDisposeSceneObjectTree } from "../../scene/sceneDispose";
import { safeTraverse } from "../../scene/sceneObjectLookups";
import { createDefaultPhongMaterial } from "../../../utils/materialDefaults";
import { logger } from "../../../utils/logger";

function buildGeomMap(nodes) {
  const map = new Map();
  const { meshes: meshDefinitions = {} } = getSceneState();
  const walk = (node) => {
    if (!node) return;
    const geoms = Array.isArray(node.geoms) ? node.geoms : [];
    geoms.forEach((g, idx) => {
      if (!g) return;
      const meshId = g.mesh || null;
      const isCollision = g.visualOnly !== true;
      if (!node.uuid) {
        logger.error("[Scene] Body node missing UUID for instanceId generation.");
        return;
      }
      const instanceId = `${node.uuid}:${g.name || meshId || idx}`;
      let meshScale = [1, 1, 1];
      if (meshDefinitions && meshId) {
        const definedScale = meshDefinitions[meshId]?.scale;
        if (Array.isArray(definedScale) && definedScale.length === 3) {
          meshScale = [...definedScale];
        }
      }
      map.set(instanceId, {
        bodyUuid: node.uuid,
        bodyName: node.name,
        meshName: meshId,
        meshId: meshDefinitions?.[meshId]?.fingerprint || null,
        materialId: g.materials?.[0] || null,
        isCollision,
        meshScale,
      });
    });
    (node.children || []).forEach(walk);
  };
  (nodes || []).forEach(walk);
  return map;
}

function makeMaterialFactory(materials) {
  const resolveDefaultMaterial = () =>
    Object.values(materials).find((mat) => mat?.name === "default_mat") || null;

  return (materialId, isCollision) => {
    if (isCollision) {
      const collisionMat = createCollisionMaterial();
      const collisionName = materials?.[materialId]?.name || "collision_mat";
      collisionMat.name = collisionName;
      return collisionMat;
    }
    const fallback =
      resolveDefaultMaterial() ||
      createDefaultPhongMaterial("default_mat", { color: [0.75, 0.75, 0.75, 1] });
    const matDef = (materialId && materials[materialId]) || fallback;
    const materialName = matDef?.name || materials[materialId]?.name || "default_mat";
    const definition = { ...matDef, name: materialName };
    return buildMaterial(definition.name, definition, () => null);
  };
}

function applyMeshUserData(obj, inst, next, meshName) {
  safeTraverse(obj, (child) => {
    if (child.isMesh) {
      child.userData = {
        ...child.userData,
        type: next.isCollision ? "collision" : "mesh",
        ...(next.isCollision ? { collisionGeomKind: "mesh" } : {}),
        meshName: meshName || child.userData?.meshName,
        bodyUuid: next.bodyUuid,
        bodyName: next.bodyName,
        selectable: true,
        ...(next.meshId ? { assetId: next.meshId } : {}),
        instanceId: inst,
        materialUuid: next.isCollision ? null : next.materialId || null,
      };
    }
  });
}

export function applyInPlaceMeshSwaps({
  bodyTree,
  fileMap,
  materials,
  objectsMap,
  previousBodies,
}) {
  try {
    const prevGeomMap = buildGeomMap(previousBodies || []);
    const nextGeomMap = buildGeomMap(bodyTree);
    const makeMaterial = makeMaterialFactory(materials);
    const objLoader = new OBJLoader();
    const stlLoader = new STLLoader();

    const scheduleSwap = (inst, next) => {
      const group = next.bodyUuid ? objectsMap[next.bodyUuid] : null;
      if (!group) return;
      const toRemove = [];
      safeTraverse(group, (child) => {
        if (child?.userData?.instanceId === inst) toRemove.push(child);
      });
      toRemove.forEach((child) => {
        try {
          releaseAndDisposeSceneObjectTree(child);
        } catch (error) {
          logger.warn("[Scene] Unexpected error during scene update", error);
        }
        if (child.parent) child.parent.remove(child);
      });

      const material = makeMaterial(next.materialId, next.isCollision);
      const meshScale = next.meshScale || [1, 1, 1];
      const meshName = next.meshName;

      if (next.meshId) {
        acquireMeshFromCache(next.meshId)
          .then((res) => {
            let objToAdd = null;
            if (res && res.isBufferGeometry) {
              const mesh = new THREE.Mesh(res.clone(), material);
              mesh.userData.assetGeometryOwned = true;
              mesh.name = meshName || "mesh";
              mesh.scale.set(...meshScale);
              objToAdd = mesh;
            } else if (res && typeof res.traverse === "function") {
              const cloned = res.clone(true);
              safeTraverse(cloned, (child) => {
                if (child.isMesh) {
                  child.userData = { ...child.userData, assetGeometryOwned: false };
                  child.material = material;
                  child.name = meshName || child.name;
                  child.scale.set(...meshScale);
                }
              });
              objToAdd = cloned;
            }
            if (!objToAdd) return;
            applyMeshUserData(objToAdd, inst, next, meshName);
            group.add(objToAdd);
          })
          .catch(() => {
            loadMesh(
              meshName,
              fileMap,
              meshScale,
              material,
              objLoader,
              stlLoader,
              (obj) => {
                applyMeshUserData(obj, inst, next, meshName);
                group.add(obj);
              },
              next.isCollision
            );
          });
      } else if (meshName) {
        loadMesh(
          meshName,
          fileMap,
          meshScale,
          material,
          objLoader,
          stlLoader,
          (obj) => {
            applyMeshUserData(obj, inst, next, meshName);
            group.add(obj);
          },
          next.isCollision
        );
      }
    };

    nextGeomMap.forEach((next, inst) => {
      const prev = prevGeomMap.get(inst);
      if (!prev) return;
      const changed =
        (prev.meshId || null) !== (next.meshId || null) ||
        (prev.meshName || null) !== (next.meshName || null);
      if (changed) scheduleSwap(inst, next);
    });
  } catch (error) {
    logger.warn("[Scene] Instance swap diff failed", error);
  }
}
